/**
 * נירמול נתוני GET /api/flight-proposals לתצוגת סיכום הזמנה (ללא שינוי API).
 */
import {
  buildFlightCardLegRowsFromDeal,
  getFlightSegmentsWithLegs,
} from "../../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";

export function dealLikeFromOffer(offer) {
  if (!offer || typeof offer !== "object") return null;
  const lf = offer.linked_flights ?? offer.linkedFlights ?? null;
  if (!lf) return null;
  return { linkedFlights: lf };
}

function formatTime(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";
  const parts = dateStr.split(" ");
  return parts.length > 1 ? parts[1] : dateStr;
}

export function shortClockFromLdsDateTime(dateStr) {
  const t = formatTime(dateStr);
  if (!t) return "";
  const m = t.match(/(\d{1,2}):(\d{2})/);
  if (!m) return t;
  const h = String(parseInt(m[1], 10)).padStart(2, "0");
  const min = m[2].padStart(2, "0");
  return `${h}:${min}`;
}

/** HH:mm מתוך שדה זמן גולמי (למשל ‎06:15) כשאין ‎depDate מלא */
function clockFromLooseTime(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = String(parseInt(m[1], 10)).padStart(2, "0");
  const min = m[2].padStart(2, "0");
  return `${h}:${min}`;
}

function departureClockFromLeg(leg) {
  if (leg?.depDate) return shortClockFromLdsDateTime(leg.depDate);
  return clockFromLooseTime(
    leg?.departure?.time ?? leg?.depTime ?? leg?.departureTime ?? ""
  );
}

function arrivalClockFromLeg(leg) {
  if (leg?.arrDate) return shortClockFromLdsDateTime(leg.arrDate);
  return clockFromLooseTime(
    leg?.arrival?.time ?? leg?.arrTime ?? leg?.arrivalTime ?? ""
  );
}

function formatDateShort(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";
  const parts = dateStr.split(" ");
  return parts[0] || "";
}

function formatDurationMinutes(min) {
  const n = Number(min);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${h}:${m < 10 ? `0${m}` : m}`;
}

function legEndpointCode(leg, end) {
  if (!leg) return "—";
  if (end === "dep") {
    return (
      leg.depAirport?.code ||
      leg.depCity ||
      leg.departure?.airport ||
      leg.from ||
      leg.origin ||
      "—"
    );
  }
  return (
    leg.arrAirport?.code ||
    leg.arrCity ||
    leg.arrival?.airport ||
    leg.to ||
    leg.destination ||
    "—"
  );
}

function airlineCodeFromLeg(leg) {
  if (!leg) return null;
  if (leg.airlineId != null && String(leg.airlineId).trim() !== "") {
    return String(leg.airlineId).trim();
  }
  if (leg.airlineCode != null && String(leg.airlineCode).trim() !== "") {
    return String(leg.airlineCode).trim();
  }
  return null;
}

/** קידומת IATA ממספר טיסה (למשל LY123 → LY) */
function airlineCodeFromFlightNumberString(num) {
  if (num == null || num === "") return null;
  const m = String(num).trim().match(/^([A-Za-z]{2,3})\d/);
  return m ? m[1].toUpperCase() : null;
}

/**
 * כשאין מבנה deal מלא ל־getFlightSegmentsWithLegs אבל יש legacyFlatLegs —
 * מחזיר אותו מבנה כמו buildFlightSegmentCards כדי לרנדר FlightCard אחיד.
 * @param {Array<{ idx: number, from: string, to: string, num: string, depT: string, arrT: string }>} legacyLegs
 */
export function segmentCardsFromLegacyLegs(legacyLegs) {
  if (!Array.isArray(legacyLegs) || legacyLegs.length === 0) return [];
  return legacyLegs.map((l) => {
    const code = airlineCodeFromFlightNumberString(l.num);
    return {
      segmentMeta: {
        id: `legacy-seg-${l.idx}`,
        labelHe: `מקטע ${l.idx}`,
        isDirect: true,
        stopLabelHe: "ישירה",
        duration: null,
        airlineCode: code,
        flightNumber: l.num || "",
        depDateDisplay: null,
      },
      legTimelines: [
        {
          key: `legacy-leg-${l.idx}`,
          depCode: l.from || "—",
          arrCode: l.to || "—",
          depClock: clockFromLooseTime(l.depT),
          arrClock: clockFromLooseTime(l.arrT),
          departureDateTime: null,
          arrivalDateTime: null,
          duration: "—",
          flightNumber: l.num || "",
          airlineCode: code,
          terminalDep: "",
          terminalArr: "",
        },
      ],
    };
  });
}

/**
 * קטעי טיסה (הלוך/חזור) + מקטעים פנימיים לציר זמן.
 * @returns {Array<{ segmentMeta: object, legTimelines: object[] }>}
 */
export function buildFlightSegmentCards(offer) {
  const deal = dealLikeFromOffer(offer);
  if (!deal) return [];
  const segLegs = getFlightSegmentsWithLegs(deal);
  const rows = buildFlightCardLegRowsFromDeal(deal);
  if (!segLegs.length) return [];

  return segLegs.map((sl, segIdx) => {
    const segmentMeta = rows[segIdx] || null;
    const legTimelines = (sl.legs || []).map((leg, li) => ({
      key: `leg-${segIdx}-${li}`,
      depCode: legEndpointCode(leg, "dep"),
      arrCode: legEndpointCode(leg, "arr"),
      depClock: departureClockFromLeg(leg),
      arrClock: arrivalClockFromLeg(leg),
      departureDateTime: leg?.depDate ?? null,
      arrivalDateTime: leg?.arrDate ?? null,
      depDateLabel: leg?.depDate ? formatDateShort(leg.depDate) : "",
      duration: formatDurationMinutes(leg?.duration),
      flightNumber: leg?.flightNumber || leg?.flightNum || "",
      airlineCode: airlineCodeFromLeg(leg),
      terminalDep: leg?.depTerminal || leg?.departure?.terminal || "",
      terminalArr: leg?.arrTerminal || leg?.arrival?.terminal || "",
    }));

    return { segmentMeta, legTimelines };
  });
}

function normalizeType(t) {
  return String(t || "")
    .trim()
    .toUpperCase();
}

/**
 * פירוט תוספות לפי קטגוריה (מחירים מוערכים מ־orig.supp.fare).
 */
export function supplementPriceBreakdown(supplement) {
  let baggage = 0;
  let trolley = 0;
  let other = 0;
  if (!supplement || typeof supplement !== "object") {
    return { baggage, trolley, other, total: 0 };
  }
  for (const entry of Object.values(supplement)) {
    if (!entry?.checked) continue;
    const fare = Number(entry?.orig?.supp?.fare);
    const f = Number.isFinite(fare) ? fare : 0;
    const type = normalizeType(entry?.orig?.supp?.type);
    if (type === "BAG" || type === "LUGGAGE" || type.includes("BAG")) {
      baggage += f;
    } else if (type === "TROLLEY" || type.includes("TROLLEY")) {
      trolley += f;
    } else {
      other += f;
    }
  }
  const total = baggage + trolley + other;
  return { baggage, trolley, other, total };
}

function paxLabelFromRef(paxRef, passengersFlat) {
  const n = Math.max(1, parseInt(String(paxRef || "1"), 10) || 1);
  if (Array.isArray(passengersFlat) && passengersFlat[n - 1]) {
    return passengersFlat[n - 1].name;
  }
  return `נוסע ${n}`;
}

/**
 * שורות שירותים (כבודה / טרולי) לתצוגה.
 * @param {object} supplement
 * @param {{ key: string, name: string, type: string }[]} passengersFlat
 */
export function mapSupplementToAncillaryRows(supplement, passengersFlat) {
  if (!supplement || typeof supplement !== "object") return [];
  const rows = [];
  let idx = 0;
  for (const [mapKey, entry] of Object.entries(supplement)) {
    if (!entry?.checked) continue;
    const supp = entry?.orig?.supp || {};
    const type = normalizeType(supp.type);
    const paxRef = supp.paxRef ?? supp.paxref ?? entry?.pax?.["@seqNum"] ?? "1";
    const assignee = paxLabelFromRef(paxRef, passengersFlat);

    let category = "extra";
    let titleHe = "תוספת";
    if (type === "BAG" || type === "LUGGAGE" || type.includes("BAG")) {
      category = "baggage";
      titleHe = "כבודה";
    } else if (type === "TROLLEY" || type.includes("TROLLEY")) {
      category = "trolley";
      titleHe = "טרולי";
    }

    const desc =
      supp.description ||
      supp.title ||
      entry?.description ||
      (typeof entry?.label === "string" ? entry.label : mapKey);

    const weight = supp.weight ? `${supp.weight}${supp.unit || "KG"}` : "";
    const fare = supp.fare != null && Number.isFinite(Number(supp.fare)) ? Number(supp.fare) : null;

    rows.push({
      key: `anc-${idx++}-${mapKey}`,
      category,
      titleHe,
      description: [desc, weight].filter(Boolean).join(" · ") || titleHe,
      assignee,
      fare,
    });
  }
  return rows;
}

export function parseOurPrice(ourPrice) {
  if (ourPrice == null) return null;
  if (typeof ourPrice === "number" && Number.isFinite(ourPrice)) return ourPrice;
  const n = Number(ourPrice);
  if (Number.isFinite(n)) return n;
  if (typeof ourPrice === "object" && ourPrice.amount != null) {
    const a = Number(ourPrice.amount);
    return Number.isFinite(a) ? a : null;
  }
  return null;
}

/**
 * שורות פירוט מחיר לסיידבר (הגיוני כשיש נתונים; אחרת חלקן מוסתרות).
 */
export function buildPriceSidebarLines({ total, currency, offer, supplement }) {
  const cur = currency ? String(currency).trim() : "";
  const extras = supplementPriceBreakdown(supplement);
  const our = parseOurPrice(offer?.our_price);
  const totalNum = total != null ? Number(total) : null;
  const hasTotal = Number.isFinite(totalNum);

  const lines = [];

  let base = null;
  if (our != null) {
    base = our;
  } else if (hasTotal && extras.total > 0 && totalNum >= extras.total) {
    base = totalNum - extras.total;
  }

  if (base != null && base > 0) {
    lines.push({ key: "base", label: "מחיר בסיס", amount: base });
  }

  if (extras.baggage > 0) {
    lines.push({ key: "bag", label: "כבודה", amount: extras.baggage });
  }
  if (extras.trolley > 0) {
    lines.push({ key: "trolley", label: "טרולי", amount: extras.trolley });
  }
  if (extras.other > 0) {
    lines.push({ key: "other", label: "תוספות", amount: extras.other });
  }

  const sumLines = lines.reduce((s, x) => s + (x.amount || 0), 0);
  const showTaxHint =
    hasTotal && sumLines > 0 && Math.abs(totalNum - sumLines) > 0.5 && totalNum > sumLines;
  if (showTaxHint) {
    lines.push({
      key: "tax",
      label: "מיסים והיטלים (משוער)",
      amount: totalNum - sumLines,
    });
  }

  return { lines, currency: cur, total: hasTotal ? totalNum : null };
}

export function formatLdsYyyymmdd(v) {
  if (v == null) return "—";
  const s = String(v).replace(/\D/g, "");
  if (s.length < 8) return String(v);
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
}

export function flattenPassengers(passengersInfo) {
  if (!Array.isArray(passengersInfo)) return [];
  const rows = [];
  passengersInfo.forEach((room, ri) => {
    const adults = room?.adults || [];
    adults.forEach((a, i) => {
      const name = [a?.firstName, a?.lastName].filter(Boolean).join(" ") || `מבוגר ${i + 1}`;
      rows.push({ key: `r${ri}-a${i}`, name, type: "מבוגר", typeKey: "adult" });
    });
    (room?.kids || []).forEach((k, i) => {
      const name = [k?.firstName, k?.lastName].filter(Boolean).join(" ") || `ילד ${i + 1}`;
      rows.push({ key: `r${ri}-k${i}`, name, type: "ילד", typeKey: "child" });
    });
    (room?.infants || []).forEach((inf, i) => {
      const name = [inf?.firstName, inf?.lastName].filter(Boolean).join(" ") || `תינוק ${i + 1}`;
      rows.push({ key: `r${ri}-i${i}`, name, type: "תינוק", typeKey: "infant" });
    });
  });
  return rows;
}
